import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { Grid, Hidden } from '@mui/material/';
import { ConfirmationDialog } from '../components/dialog';
import PanelHeader from '../components/panelheader/panelHeader';
import PageLayout from '../pagelayout';

import LanguageSwitch from '../i18n/languageSwitch';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import { useUpdater } from '../api/useUpdater';
import { BrukerinfoDto } from '../api/generated/praksissted-generated-api';
import Spinner from '../components/spinner/spinner';
import { FakeLockedInput } from '../components/input';
import Button from '../components/button';
import translations from './settings.i18n';

const SettingsPage = () => {
  const { formatMessage } = useIntl();
  const { brukerApi } = useContext(PraksisstedApiContext);
  const { data: userinfo, loading } = useLoader(
    () => brukerApi.brukerinfoGet(),
    [],
  );

  return (
    <PageLayout
      isIndented={true}
      title={formatMessage({ id: translations.title.id })}
      subtitle={formatMessage({ id: translations.subtitle.id })}
    >
      {loading && <Spinner />}
      {userinfo && (
        <>
          <Userinfo userinfo={userinfo} />
          <AlpSettings />
          <SlettPraksisstedbruker />
        </>
      )}
    </PageLayout>
  );
};

const Userinfo: React.FC<
  React.PropsWithChildren<{ userinfo: BrukerinfoDto }>
> = ({ userinfo }) => {
  const { fornavn, etternavn, telefonnummer, epost } = userinfo;
  const { formatMessage } = useIntl();
  return (
    <Grid container={true} spacing={3}>
      <Grid item={true} xs={12}>
        <PanelHeader
          title={formatMessage({ id: translations.userTitle.id })}
          subtitle={formatMessage({ id: translations.userSubtitle.id })}
        />
      </Grid>

      <Grid item={true} sm={6} xs={12}>
        <FakeLockedInput
          label={formatMessage({ id: translations.userName.id })}
          value={fornavn + ' ' + etternavn}
        />
      </Grid>
      <Hidden only={['xs']}>
        <Grid item={true} sm={6} />
      </Hidden>

      <Grid item={true} sm={6} xs={12}>
        <FakeLockedInput
          label={formatMessage({ id: translations.useremail.id })}
          value={epost}
        />
      </Grid>
      <Grid item={true} sm={6} xs={12}>
        <FakeLockedInput
          label={formatMessage({ id: translations.userphone.id })}
          value={telefonnummer}
        />
      </Grid>
    </Grid>
  );
};

const AlpSettings: React.FC<React.PropsWithChildren<{}>> = () => {
  const { brukerApi } = useContext(PraksisstedApiContext);
  const { doPost } = useUpdater();

  const postLanguageChanged = (spraakvalg: string) => {
    doPost(
      async () =>
        await brukerApi.brukerinfoInnstillingerPut({
          brukerinnstillingerDto: { spraakvalg },
        }),
    );
  };

  const { formatMessage } = useIntl();
  return (
    <>
      <PanelHeader
        title={formatMessage({ id: translations.portalTitle.id })}
        subtitle={formatMessage({ id: translations.portalSubtitle.id })}
      />

      <Grid container={true}>
        <LanguageSwitch onLanguageChanged={postLanguageChanged} />
      </Grid>
    </>
  );
};

const SlettPraksisstedbruker: React.FC<React.PropsWithChildren<{}>> = () => {
  const { praksisstedApi } = useContext(PraksisstedApiContext);
  const { doPost } = useUpdater();

  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  const deleteAccount = (confirmed: boolean) => {
    setDeleteConfirmation(false);
    if (confirmed) {
      doPost(async () => await praksisstedApi.slettmegDelete());
      window.location.href = '/';
    }
  };

  const { formatMessage } = useIntl();
  // TODO: Knapp skal være hvit og ha rød kant og rød skrift
  return (
    <>
      <ConfirmationDialog
        isOpen={deleteConfirmation}
        onClose={deleteAccount}
        title={formatMessage({
          id: translations.deleteConfirmationTitle.id,
        })}
      >
        <p>
          {formatMessage({ id: translations.deleteConfirmationMessage.id })}
        </p>
      </ConfirmationDialog>

      <PanelHeader
        title={formatMessage({ id: translations.deleteTitle.id })}
        subtitle={formatMessage({ id: translations.deleteSubtitle.id })}
      />
      <Button.Quinary
        onClick={() => {
          setDeleteConfirmation(true);
        }}
      >
        {formatMessage({ id: translations.deleteButton.id })}
      </Button.Quinary>
    </>
  );
};

export default SettingsPage;
